import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ActionMenu, ConfirmDialog, Dialog, DialogError, Field, Toggle } from '../../components/admin/AdminActions';
import { inputClass, primaryButton, secondaryButton } from '../../components/admin/AdminUI';
import { Card } from '../../components/Card';
import { StatusBadge } from '../../components/StatusBadge';
import type { AdminAppAccessResponse, AdminAppState, AdminMemberAccess } from '../../types/api';
import { useAdminResource } from './useAdminResource';
import { IdentityCell, StatusPill } from '../../components/admin/TableCells';
import { AdminRequestError, adminRequest, errorMessage, useAdminAction } from './adminRequest';
import { DirectoryError } from './parts';
import { humanize } from './labels';

export const MEMBER_ROLES = ['owner', 'admin', 'member', 'viewer'] as const;
const APP_TIERS = ['admin', 'member', 'viewer'] as const;

export interface ManagedWorkspace {
  id: string;
  name: string;
  type: string;
  status: string;
}

export function workspaceKind(type: string): 'business' | 'personal' {
  return type === 'personal' ? 'personal' : 'business';
}

type WorkspaceDialog = 'rename' | 'type' | 'plan' | 'close' | 'reopen' | null;

/** The workspace's action menu (rename, type, plan, close/reopen) and its dialogs. */
export function WorkspaceActions({
  workspace,
  onChanged,
  currentPlanCode,
}: {
  workspace: ManagedWorkspace;
  onChanged: () => void;
  currentPlanCode?: string | null;
}) {
  const [dialog, setDialog] = useState<WorkspaceDialog>(null);
  const closed = workspace.status === 'closed';
  const close = () => setDialog(null);
  const done = () => {
    close();
    onChanged();
  };
  const kind = workspaceKind(workspace.type);
  const other = kind === 'business' ? 'personal' : 'business';
  const closeAction = useAdminAction();
  const reopenAction = useAdminAction();

  return (
    <span onClick={(e) => e.stopPropagation()} className="inline-flex">
      <ActionMenu
        label={`Actions for ${workspace.name}`}
        items={[
          { label: 'Rename', onSelect: () => setDialog('rename') },
          {
            label: `Change to a ${other} workspace…`,
            onSelect: () => setDialog('type'),
            disabled: closed,
            hint: 'Reopen it first',
          },
          { label: 'Change plan…', onSelect: () => setDialog('plan'), disabled: closed, hint: 'Reopen it first' },
          { label: 'Reopen workspace', onSelect: () => setDialog('reopen'), hidden: !closed },
          { label: 'Close workspace…', onSelect: () => setDialog('close'), hidden: closed, danger: true },
        ]}
      />
      <RenameDialog open={dialog === 'rename'} workspace={workspace} onClose={close} onDone={done} />
      <TypeDialog open={dialog === 'type'} workspace={workspace} target={other} onClose={close} onDone={done} />
      <PlanDialog open={dialog === 'plan'} workspace={workspace} currentPlanCode={currentPlanCode ?? null} onClose={close} onDone={done} />
      <ConfirmDialog
        open={dialog === 'close'}
        title="Close this workspace?"
        danger
        confirmText={workspace.name}
        body={
          <>
            <p>
              Nobody can sign in to <span className="font-medium text-slate-900">{workspace.name}</span> or open its
              apps while it is closed. Its sessions' refresh tokens and app connections are revoked.
            </p>
            <p>
              Nothing is deleted and members are kept, so reopening restores access. A paid subscription is set to
              cancel at the end of its period; reopening does not restart billing.
            </p>
          </>
        }
        confirmLabel="Close workspace"
        busy={closeAction.busy}
        error={closeAction.error}
        onClose={close}
        onConfirm={async () => {
          const ok = await closeAction.run(() => adminRequest('POST', `/workspaces/${workspace.id}/close`, {}));
          if (ok !== undefined) {
            toast.success('Workspace closed');
            done();
          }
        }}
      />
      <ConfirmDialog
        open={dialog === 'reopen'}
        title="Reopen this workspace?"
        body={<p>Members can sign in to {workspace.name} and open its apps again.</p>}
        confirmLabel="Reopen"
        busy={reopenAction.busy}
        error={reopenAction.error}
        onClose={close}
        onConfirm={async () => {
          const ok = await reopenAction.run(() => adminRequest('POST', `/workspaces/${workspace.id}/reopen`, {}));
          if (ok !== undefined) {
            toast.success('Workspace reopened');
            done();
          }
        }}
      />
    </span>
  );
}

function RenameDialog({ open, workspace, onClose, onDone }: { open: boolean; workspace: ManagedWorkspace; onClose: () => void; onDone: () => void }) {
  const action = useAdminAction();
  const [name, setName] = useState(workspace.name);
  useEffect(() => {
    if (open) {
      setName(workspace.name);
      action.reset();
    }
  }, [open, workspace.name, action.reset]);
  const save = async () => {
    const ok = await action.run(() => adminRequest('PATCH', `/workspaces/${workspace.id}`, { name: name.trim() }));
    if (ok !== undefined) {
      toast.success('Workspace renamed');
      onDone();
    }
  };
  return (
    <Dialog
      open={open}
      title="Rename workspace"
      onClose={onClose}
      busy={action.busy}
      footer={
        <>
          <button type="button" className={secondaryButton} onClick={onClose} disabled={action.busy}>
            Cancel
          </button>
          <button type="button" className={primaryButton} onClick={save} disabled={action.busy || !name.trim()}>
            {action.busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <Field label="Workspace name">
        <input aria-label="Workspace name" value={name} onChange={(e) => setName(e.target.value)} maxLength={255} className={inputClass} />
      </Field>
      <DialogError message={action.error} />
    </Dialog>
  );
}

function TypeDialog({
  open,
  workspace,
  target,
  onClose,
  onDone,
}: {
  open: boolean;
  workspace: ManagedWorkspace;
  target: 'business' | 'personal';
  onClose: () => void;
  onDone: () => void;
}) {
  const action = useAdminAction();
  useEffect(() => {
    if (open) action.reset();
  }, [open, action.reset]);
  return (
    <ConfirmDialog
      open={open}
      title={`Change to a ${target} workspace?`}
      body={
        <>
          <p>
            {workspace.name} will be treated as a {target} workspace: the apps and outcomes it is offered follow the
            new type ({target === 'business' ? 'CRM' : 'Home'} becomes its always-on app).
          </p>
          <p>
            If its plan is not sold to {target} workspaces it moves to the {target} free plan. A workspace paying
            through Stripe must change plan first.
          </p>
        </>
      }
      confirmLabel={`Make it ${target}`}
      busy={action.busy}
      error={action.error}
      onClose={onClose}
      onConfirm={async () => {
        const res = await action.run(() =>
          adminRequest<{ planMovedTo?: string }>('PATCH', `/workspaces/${workspace.id}`, { type: target }),
        );
        if (res) {
          toast.success(res.planMovedTo ? `Now a ${target} workspace, on the ${res.planMovedTo} plan` : `Now a ${target} workspace`);
          onDone();
        }
      }}
    />
  );
}

interface PlanListResponse {
  plans: Array<{ code: string; name: string; status: string; displayOrder: number }>;
}

function PlanDialog({
  open,
  workspace,
  currentPlanCode,
  onClose,
  onDone,
}: {
  open: boolean;
  workspace: ManagedWorkspace;
  currentPlanCode: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const action = useAdminAction();
  const [plans, setPlans] = useState<PlanListResponse['plans']>([]);
  const [planCode, setPlanCode] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    action.reset();
    setPlanCode(currentPlanCode ?? '');
    setLoadError(null);
    let live = true;
    void (async () => {
      try {
        const { authFetchWithRecovery } = await import('../../utils/sessionRecovery');
        const { response } = await authFetchWithRecovery('/api/admin/plans?status=active&limit=200');
        if (!live || !response) return;
        const body = (await response.json()) as PlanListResponse;
        setPlans([...(body.plans ?? [])].sort((a, b) => a.displayOrder - b.displayOrder));
      } catch (err) {
        if (live) setLoadError(errorMessage(err));
      }
    })();
    return () => {
      live = false;
    };
  }, [open, currentPlanCode, action.reset]);

  const save = async () => {
    const res = await action.run(() =>
      adminRequest<{ changed: boolean; via?: string; effective?: string }>('PUT', `/workspaces/${workspace.id}/plan`, { planCode }),
    );
    if (!res) return;
    toast.success(
      !res.changed
        ? 'Already on that plan'
        : res.effective === 'next_cycle'
          ? 'Plan change scheduled for the end of the billing period'
          : 'Plan changed',
    );
    onDone();
  };

  return (
    <Dialog
      open={open}
      title="Change plan"
      description={workspace.name}
      onClose={onClose}
      busy={action.busy}
      footer={
        <>
          <button type="button" className={secondaryButton} onClick={onClose} disabled={action.busy}>
            Cancel
          </button>
          <button type="button" className={primaryButton} onClick={save} disabled={action.busy || !planCode}>
            {action.busy ? 'Changing…' : 'Change plan'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Plan">
          <select aria-label="Plan" value={planCode} onChange={(e) => setPlanCode(e.target.value)} className={inputClass}>
            <option value="" disabled>
              Choose a plan
            </option>
            {plans.map((p) => (
              <option key={p.code} value={p.code}>
                {p.name} ({p.code}){p.code === currentPlanCode ? ' — current' : ''}
              </option>
            ))}
          </select>
        </Field>
        <p className="text-xs text-slate-500">
          A workspace that pays through Stripe is billed for the change (upgrades now, prorated; downgrades at the end
          of the period). Any other workspace can move to a plan that takes effect without payment — a paid plan needs
          the owner to buy it from Billing.
        </p>
      </div>
      <DialogError message={loadError ?? action.error} />
    </Dialog>
  );
}

// ── members ─────────────────────────────────────────────────────────────────

export function AddMemberDialog({
  open,
  workspace,
  onClose,
  onDone,
}: {
  open: boolean;
  workspace: ManagedWorkspace;
  onClose: () => void;
  onDone: () => void;
}) {
  const action = useAdminAction();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<(typeof MEMBER_ROLES)[number]>('member');
  useEffect(() => {
    if (open) {
      setEmail('');
      setRole('member');
      action.reset();
    }
  }, [open, action.reset]);
  const save = async () => {
    const ok = await action.run(() =>
      adminRequest('POST', `/workspaces/${workspace.id}/members`, { email: email.trim(), role }),
    );
    if (ok !== undefined) {
      toast.success('Added to the workspace');
      onDone();
    }
  };
  return (
    <Dialog
      open={open}
      title="Add a member"
      description={`Adds an existing Central account to ${workspace.name} straight away (no invitation email).`}
      onClose={onClose}
      busy={action.busy}
      footer={
        <>
          <button type="button" className={secondaryButton} onClick={onClose} disabled={action.busy}>
            Cancel
          </button>
          <button type="button" className={primaryButton} onClick={save} disabled={action.busy || !email.trim()}>
            {action.busy ? 'Adding…' : 'Add member'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Their Central email" help="No account yet? Create one under Users first.">
          <input aria-label="Their Central email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
        </Field>
        <Field label="Role">
          <select aria-label="Role" value={role} onChange={(e) => setRole(e.target.value as (typeof MEMBER_ROLES)[number])} className={inputClass}>
            {MEMBER_ROLES.map((r) => (
              <option key={r} value={r}>
                {humanize(r)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <DialogError message={action.error} />
    </Dialog>
  );
}

/** Add the person on a user page to some workspace, chosen by name. */
export function AddToWorkspaceDialog({
  open,
  userEmail,
  onClose,
  onDone,
}: {
  open: boolean;
  userEmail: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const action = useAdminAction();
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [role, setRole] = useState<(typeof MEMBER_ROLES)[number]>('member');
  const { data } = useAdminResource<{ workspaces: Array<{ id: string; name: string; type: string; status: string }> }>(
    `/workspaces?limit=25${query ? `&q=${encodeURIComponent(query)}` : ''}`,
  );
  useEffect(() => {
    if (open) {
      setQ('');
      setQuery('');
      setWorkspaceId('');
      setRole('member');
      action.reset();
    }
  }, [open, action.reset]);
  if (!open) return null;
  const save = async () => {
    const ok = await action.run(() => adminRequest('POST', `/workspaces/${workspaceId}/members`, { email: userEmail, role }));
    if (ok !== undefined) {
      toast.success('Added to the workspace');
      onDone();
    }
  };
  return (
    <Dialog
      open={open}
      title="Add to a workspace"
      description={userEmail}
      onClose={onClose}
      busy={action.busy}
      footer={
        <>
          <button type="button" className={secondaryButton} onClick={onClose} disabled={action.busy}>
            Cancel
          </button>
          <button type="button" className={primaryButton} onClick={save} disabled={action.busy || !workspaceId}>
            {action.busy ? 'Adding…' : 'Add'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Find a workspace">
          <div className="flex gap-2">
            <input
              aria-label="Find a workspace"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setQuery(q.trim());
              }}
              className={inputClass}
              placeholder="Workspace name"
            />
            <button type="button" className={secondaryButton} onClick={() => setQuery(q.trim())}>
              Search
            </button>
          </div>
        </Field>
        <Field label="Workspace">
          <select aria-label="Workspace" value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)} className={inputClass}>
            <option value="">Choose…</option>
            {(data?.workspaces ?? []).map((w) => (
              <option key={w.id} value={w.id} disabled={w.status === 'closed'}>
                {w.name} ({workspaceKind(w.type)}){w.status === 'closed' ? ' — closed' : ''}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Role">
          <select aria-label="Role" value={role} onChange={(e) => setRole(e.target.value as (typeof MEMBER_ROLES)[number])} className={inputClass}>
            {MEMBER_ROLES.map((r) => (
              <option key={r} value={r}>
                {humanize(r)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <DialogError message={action.error} />
    </Dialog>
  );
}

type MemberDialog = 'role' | 'remove' | 'apps' | null;

/** One member's action menu: change role, app access, remove. */
export function MemberActions({
  workspace,
  member,
  apps,
  onChanged,
}: {
  workspace: ManagedWorkspace;
  member: { userId: string; email: string | null; role: string; status: string; appRoles?: Record<string, string> };
  apps: AdminAppState[] | null;
  onChanged: () => void;
}) {
  const [dialog, setDialog] = useState<MemberDialog>(null);
  const [role, setRole] = useState(member.role);
  const roleAction = useAdminAction();
  const removeAction = useAdminAction();
  const who = member.email ?? member.userId;
  const close = () => setDialog(null);
  const done = () => {
    close();
    onChanged();
  };
  const ownerOfBusiness = member.role === 'owner' && workspaceKind(workspace.type) === 'business';
  useEffect(() => {
    if (dialog === 'role') {
      setRole(member.role);
      roleAction.reset();
    }
    if (dialog === 'remove') removeAction.reset();
  }, [dialog, member.role, roleAction.reset, removeAction.reset]);

  return (
    <span onClick={(e) => e.stopPropagation()} className="inline-flex">
      <ActionMenu
        label={`Actions for member ${who}`}
        items={[
          { label: 'Change role…', onSelect: () => setDialog('role') },
          {
            label: 'App access…',
            onSelect: () => setDialog('apps'),
            disabled: ownerOfBusiness || member.status !== 'active' || !apps,
            hint: ownerOfBusiness
              ? 'The owner has every app through ownership'
              : member.status !== 'active'
                ? 'Only active members have app access'
                : 'Edit app access from the workspace page',
          },
          { label: 'Remove from workspace…', onSelect: () => setDialog('remove'), danger: true },
        ]}
      />
      <Dialog
        open={dialog === 'role'}
        title="Change role"
        description={`${who} in ${workspace.name}`}
        onClose={close}
        busy={roleAction.busy}
        footer={
          <>
            <button type="button" className={secondaryButton} onClick={close} disabled={roleAction.busy}>
              Cancel
            </button>
            <button
              type="button"
              className={primaryButton}
              disabled={roleAction.busy || role === member.role}
              onClick={async () => {
                const ok = await roleAction.run(() =>
                  adminRequest('PATCH', `/workspaces/${workspace.id}/members/${member.userId}`, { role }),
                );
                if (ok !== undefined) {
                  toast.success(`Now ${humanize(role).toLowerCase()}`);
                  done();
                }
              }}
            >
              {roleAction.busy ? 'Saving…' : 'Save role'}
            </button>
          </>
        }
      >
        <Field label="Role" help="A workspace always keeps at least one owner.">
          <select aria-label="Role" value={role} onChange={(e) => setRole(e.target.value)} className={inputClass}>
            {MEMBER_ROLES.map((r) => (
              <option key={r} value={r}>
                {humanize(r)}
              </option>
            ))}
          </select>
        </Field>
        <DialogError message={roleAction.error} />
      </Dialog>
      <ConfirmDialog
        open={dialog === 'remove'}
        title="Remove from workspace?"
        danger
        body={
          <p>
            {who} loses access to {workspace.name} and its apps, and leaves its teams. Their Central account is not
            affected. A workspace always keeps at least one owner.
          </p>
        }
        confirmLabel="Remove"
        busy={removeAction.busy}
        error={removeAction.error}
        onClose={close}
        onConfirm={async () => {
          const ok = await removeAction.run(() =>
            adminRequest('DELETE', `/workspaces/${workspace.id}/members/${member.userId}`),
          );
          if (ok !== undefined) {
            toast.success('Removed from the workspace');
            done();
          }
        }}
      />
      {apps && (
        <MemberAppRolesDialog
          open={dialog === 'apps'}
          workspace={workspace}
          member={{ userId: member.userId, email: member.email, appRoles: member.appRoles ?? {} }}
          apps={apps}
          onClose={close}
          onDone={done}
        />
      )}
    </span>
  );
}

/** Set a member's role inside each app the workspace has on. */
function MemberAppRolesDialog({
  open,
  workspace,
  member,
  apps,
  onClose,
  onDone,
}: {
  open: boolean;
  workspace: ManagedWorkspace;
  member: { userId: string; email: string | null; appRoles: Record<string, string> };
  apps: AdminAppState[];
  onClose: () => void;
  onDone: () => void;
}) {
  const action = useAdminAction();
  const [draft, setDraft] = useState<Record<string, string>>(member.appRoles);
  useEffect(() => {
    if (open) {
      setDraft(member.appRoles);
      action.reset();
    }
  }, [open, member.appRoles, action.reset]);
  const wildcard = member.appRoles['*'];
  // Apps the workspace has on, plus any the member still holds (dormant grants).
  const rows = useMemo(() => {
    const listed = apps.filter((a) => a.applicable && (a.on || a.appId in member.appRoles));
    return listed;
  }, [apps, member.appRoles]);

  const save = async () => {
    const ok = await action.run(() =>
      adminRequest('PUT', `/workspaces/${workspace.id}/members/${member.userId}/app-roles`, { appRoles: draft }),
    );
    if (ok !== undefined) {
      toast.success('App access saved');
      onDone();
    }
  };

  return (
    <Dialog
      open={open}
      wide
      title="App access"
      description={`${member.email ?? member.userId} in ${workspace.name}`}
      onClose={onClose}
      busy={action.busy}
      footer={
        <>
          <button type="button" className={secondaryButton} onClick={onClose} disabled={action.busy}>
            Cancel
          </button>
          <button type="button" className={primaryButton} onClick={save} disabled={action.busy}>
            {action.busy ? 'Saving…' : 'Save app access'}
          </button>
        </>
      }
    >
      {wildcard && (
        <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-inset ring-amber-600/20">
          They hold the all-apps grant ({wildcard}). It is kept unless you remove it below.
        </p>
      )}
      <table className="min-w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="py-2 pr-4 font-medium">App</th>
            <th className="py-2 font-medium">Their role in the app</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {wildcard && (
            <tr>
              <td className="py-2 pr-4 font-medium text-slate-800">All apps</td>
              <td className="py-2">
                <label className="flex items-center gap-2 text-slate-700">
                  <input
                    type="checkbox"
                    checked={'*' in draft}
                    onChange={(e) => {
                      const next = { ...draft };
                      if (e.target.checked) next['*'] = wildcard;
                      else delete next['*'];
                      setDraft(next);
                    }}
                  />
                  Keep the all-apps grant
                </label>
              </td>
            </tr>
          )}
          {rows.map((app) => (
            <tr key={app.appId}>
              <td className="py-2 pr-4">
                <span className="font-medium text-slate-800">{app.displayName}</span>
                {!app.on && <span className="ml-2 text-xs text-amber-700">(app is off for this workspace)</span>}
              </td>
              <td className="py-2">
                <select
                  aria-label={`Role in ${app.displayName}`}
                  value={draft[app.appId] ?? ''}
                  onChange={(e) => {
                    const next = { ...draft };
                    if (e.target.value) next[app.appId] = e.target.value;
                    else delete next[app.appId];
                    setDraft(next);
                  }}
                  className={`${inputClass} max-w-[12rem]`}
                >
                  <option value="">No access</option>
                  {APP_TIERS.map((t) => (
                    <option key={t} value={t}>
                      {humanize(t)}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && <p className="text-sm text-slate-500">This workspace has no apps switched on yet.</p>}
      <DialogError message={action.error} />
    </Dialog>
  );
}

// ── app access card ─────────────────────────────────────────────────────────

const OFF_REASON: Record<string, string> = {
  outcome: 'Off — no outcome that brings it is on',
  requiredEntitlement: 'Off — the plan does not include it',
  workspaceType: 'Not offered to this workspace type',
  status: 'Not available',
  isUserFacing: 'Not user-facing',
};

interface PendingAppChange {
  app: AdminAppState;
  enabled: boolean;
  outcome?: string;
  pastPlan?: { currentPlan: string | null; requiredPlan: string | null };
}

/**
 * Which apps the workspace has on, and the outcomes that decide it. Central
 * switches apps per OUTCOME, so every switch says exactly which outcomes it
 * turns on or off and which other apps come with them.
 */
export function AppAccessCard({
  workspace,
  onChanged,
  onLoaded,
}: {
  workspace: ManagedWorkspace;
  onChanged: () => void;
  onLoaded?: (access: AdminAppAccessResponse | null) => void;
}) {
  const { data, error, loading, reload } = useAdminResource<AdminAppAccessResponse>(`/workspaces/${workspace.id}/app-access`);
  const [pending, setPending] = useState<PendingAppChange | null>(null);
  const [pendingOutcome, setPendingOutcome] = useState<{ code: string; enabled: boolean } | null>(null);
  const [reason, setReason] = useState('');
  const action = useAdminAction();
  useEffect(() => {
    onLoaded?.(data ?? null);
  }, [data, onLoaded]);

  const outcomeName = (code: string) => data?.outcomes.find((o) => o.code === code)?.displayName ?? code;
  const appName = (id: string) => data?.apps.find((a) => a.appId === id)?.displayName ?? id;

  const refresh = () => {
    reload();
    onChanged();
  };

  const submitApp = async (change: PendingAppChange, override = false) => {
    try {
      const res = await adminRequest<{ on: boolean; outcomes: string[]; stillOffBecause?: string | null }>(
        'PUT',
        `/workspaces/${workspace.id}/apps/${change.app.appId}`,
        {
          enabled: change.enabled,
          ...(change.outcome ? { outcome: change.outcome } : {}),
          ...(override ? { override: true, reason: reason.trim() } : {}),
        },
      );
      if (change.enabled && !res.on) {
        toast.error(
          `${outcomeName(res.outcomes[0] ?? '')} is on, but ${change.app.displayName} also needs a plan that includes it`,
        );
      } else {
        toast.success(`${change.app.displayName} switched ${change.enabled ? 'on' : 'off'}`);
      }
      setPending(null);
      refresh();
    } catch (err) {
      if (err instanceof AdminRequestError && err.code === 'UPGRADE_REQUIRED') {
        setPending({
          ...change,
          pastPlan: {
            currentPlan: (err.details?.['currentPlan'] as string | null) ?? null,
            requiredPlan: (err.details?.['requiredPlan'] as string | null) ?? null,
          },
        });
        throw err;
      }
      throw err;
    }
  };

  if (loading) {
    return (
      <Card title="App access">
        <p role="status" className="text-sm text-slate-500">
          Loading apps…
        </p>
      </Card>
    );
  }
  if (error || !data) {
    return (
      <Card title="App access">
        {error ? <DirectoryError error={error} what="App access" onRetry={reload} /> : null}
      </Card>
    );
  }

  const applicable = data.apps.filter((a) => a.applicable);
  const pendingOutcomeApps = pending?.enabled
    ? data.outcomes.find((o) => o.code === (pending.outcome ?? pending.app.availableThrough[0]))?.apps ?? []
    : [];

  return (
    <Card
      title={`App access (${applicable.filter((a) => a.on).length} on)`}
      description="Switch apps on or off for this workspace. Set each member's role inside an app from their row in Members."
    >
      {data.predatesOutcomes && (
        <p className="mb-3 rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-800 ring-1 ring-inset ring-sky-600/20">
          This workspace predates outcomes, so every app for its type is on. Switches cannot turn them off.
        </p>
      )}
      {!data.entitlementsKnown && (
        <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-inset ring-amber-600/20">
          This workspace has no plan record yet, so paid-app entitlements are not known.
        </p>
      )}
      <ul className="divide-y divide-slate-100" data-testid="app-access-list">
        {applicable.map((app) => {
          const locked = app.baseline || (app.on && app.onBecause === 'predates_outcomes') || workspace.status === 'closed';
          return (
            <li key={app.appId} className="flex items-center justify-between gap-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900">{app.displayName}</p>
                <p className="text-xs text-slate-500">
                  {app.on
                    ? app.onBecause === 'baseline'
                      ? `Always on for ${data.workspace.kind} workspaces`
                      : app.onBecause === 'predates_outcomes'
                        ? 'On — workspace predates outcomes'
                        : `On through ${app.viaOutcomes.map(outcomeName).join(', ')}`
                    : OFF_REASON[app.offBecause ?? 'outcome'] ?? 'Off'}
                </p>
              </div>
              <Toggle
                checked={app.on}
                label={`${app.displayName} for this workspace`}
                disabled={locked}
                title={locked ? 'This app cannot be switched here' : undefined}
                onChange={(next) => {
                  action.reset();
                  setReason('');
                  setPending({ app, enabled: next, ...(next ? { outcome: [...app.availableThrough].sort((a, b) => (data.outcomes.find((o) => o.code === a)?.apps.length ?? 0) - (data.outcomes.find((o) => o.code === b)?.apps.length ?? 0))[0] } : {}) });
                }}
              />
            </li>
          );
        })}
      </ul>

      <h4 className="mt-6 mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Outcomes</h4>
      <ul className="divide-y divide-slate-100" data-testid="outcome-list">
        {data.outcomes.map((o) => {
          const on = o.state === 'active' || o.state === 'trialing';
          return (
            <li key={o.code} className="flex items-center justify-between gap-4 py-2.5">
              <div className="min-w-0">
                <p className="text-sm text-slate-900">
                  {o.displayName}{' '}
                  {o.state === 'trialing' && <StatusBadge status="info" label="Trial" />}
                  {o.state === 'lapsed' && <StatusBadge status="warning" label="Trial ended" />}
                </p>
                <p className="text-xs text-slate-500">Brings {o.apps.map(appName).join(', ') || 'no apps'}</p>
              </div>
              <Toggle
                checked={on}
                label={`Outcome ${o.displayName}`}
                disabled={workspace.status === 'closed' && !on}
                onChange={(next) => {
                  action.reset();
                  setReason('');
                  setPendingOutcome({ code: o.code, enabled: next });
                }}
              />
            </li>
          );
        })}
      </ul>

      {pending && (
        <ConfirmDialog
          open
          title={`Switch ${pending.app.displayName} ${pending.enabled ? 'on' : 'off'}?`}
          danger={!pending.enabled}
          body={
            pending.enabled ? (
              <>
                <p>Central turns apps on through outcomes.</p>
                {pending.app.availableThrough.length > 1 && (
                  <Field label="Through which outcome">
                    <select
                      aria-label="Through which outcome"
                      value={pending.outcome}
                      onChange={(e) => setPending({ ...pending, outcome: e.target.value })}
                      className={inputClass}
                    >
                      {pending.app.availableThrough.map((code) => (
                        <option key={code} value={code}>
                          {outcomeName(code)}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
                <p>
                  Switching on <span className="font-medium text-slate-900">{outcomeName(pending.outcome ?? '')}</span>{' '}
                  turns on: {pendingOutcomeApps.map(appName).join(', ')}.
                </p>
                {pending.pastPlan && (
                  <div className="rounded-lg bg-amber-50 p-3 text-amber-900 ring-1 ring-inset ring-amber-600/20">
                    <p>
                      The plan ({pending.pastPlan.currentPlan ?? 'current'}) does not include this outcome
                      {pending.pastPlan.requiredPlan ? `; ${pending.pastPlan.requiredPlan} does` : ''}. You can switch it
                      on anyway; say why for the audit log.
                    </p>
                    <input
                      aria-label="Reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className={`${inputClass} mt-2`}
                      placeholder="Reason"
                    />
                  </div>
                )}
              </>
            ) : (
              <p>
                This switches off {pending.app.viaOutcomes.map(outcomeName).join(', ')}, which also turns off the other
                apps those outcomes bring unless something else keeps them on.
              </p>
            )
          }
          confirmLabel={pending.pastPlan ? 'Switch on anyway' : pending.enabled ? 'Switch on' : 'Switch off'}
          busy={action.busy}
          error={action.error}
          onClose={() => setPending(null)}
          onConfirm={async () => {
            if (pending.pastPlan && !reason.trim()) {
              action.setError('Say why you are switching this on past the plan.');
              return;
            }
            await action.run(() => submitApp(pending, !!pending.pastPlan)).catch(() => undefined);
          }}
        />
      )}

      {pendingOutcome && (
        <OutcomeConfirm
          workspace={workspace}
          code={pendingOutcome.code}
          enabled={pendingOutcome.enabled}
          name={outcomeName(pendingOutcome.code)}
          apps={(data.outcomes.find((o) => o.code === pendingOutcome.code)?.apps ?? []).map(appName)}
          onClose={() => setPendingOutcome(null)}
          onDone={() => {
            setPendingOutcome(null);
            refresh();
          }}
        />
      )}
    </Card>
  );
}

function OutcomeConfirm({
  workspace,
  code,
  enabled,
  name,
  apps,
  onClose,
  onDone,
}: {
  workspace: ManagedWorkspace;
  code: string;
  enabled: boolean;
  name: string;
  apps: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const action = useAdminAction();
  const [pastPlan, setPastPlan] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const submit = async () => {
    if (pastPlan !== null && !reason.trim()) {
      action.setError('Say why you are switching this on past the plan.');
      return;
    }
    await action.run(async () => {
      try {
        await adminRequest('PUT', `/workspaces/${workspace.id}/outcomes/${code}`, {
          enabled,
          ...(pastPlan !== null ? { override: true, reason: reason.trim() } : {}),
        });
        toast.success(`${name} switched ${enabled ? 'on' : 'off'}`);
        onDone();
      } catch (err) {
        if (err instanceof AdminRequestError && err.code === 'UPGRADE_REQUIRED') {
          setPastPlan((err.details?.['requiredPlan'] as string | null) ?? '');
        }
        throw err;
      }
    });
  };
  return (
    <ConfirmDialog
      open
      title={`Switch ${name} ${enabled ? 'on' : 'off'}?`}
      danger={!enabled}
      body={
        <>
          <p>
            {enabled ? 'Turns on' : 'Turns off'}: {apps.join(', ') || 'no apps'}.
          </p>
          {pastPlan !== null && (
            <div className="rounded-lg bg-amber-50 p-3 text-amber-900 ring-1 ring-inset ring-amber-600/20">
              <p>
                The workspace's plan does not include this outcome{pastPlan ? ` (${pastPlan} does)` : ''}. Switch it on
                anyway? Say why for the audit log.
              </p>
              <input aria-label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} className={`${inputClass} mt-2`} placeholder="Reason" />
            </div>
          )}
        </>
      }
      confirmLabel={pastPlan !== null ? 'Switch on anyway' : enabled ? 'Switch on' : 'Switch off'}
      busy={action.busy}
      error={action.error}
      onClose={onClose}
      onConfirm={submit}
    />
  );
}

/** Members table body rows with management (used on the workspace page). */
export function ManagedMembersList({
  workspace,
  members,
  apps,
  appRolesByUser,
  onChanged,
}: {
  workspace: ManagedWorkspace;
  members: Array<{ userId: string; email: string | null; name: string | null; role: string; status: string; billingClass: string | null }>;
  apps: AdminAppState[] | null;
  appRolesByUser: Map<string, AdminMemberAccess>;
  onChanged: () => void;
}) {
  if (members.length === 0) return <p className="text-sm text-slate-500">This workspace has no members.</p>;
  return (
    <div className="overflow-x-auto rounded-xl ring-1 ring-slate-200">
      <table className="min-w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            {['Member', 'Role', 'Status', 'Apps', ''].map((h) => (
              <th key={h} className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white">
          {members.map((m) => {
            const access = appRolesByUser.get(m.userId);
            const appRoles = access?.appRoles ?? {};
            const appCount = Object.keys(appRoles).filter((k) => k !== '*').length;
            return (
              <tr key={m.userId} data-testid="member-row" className="group transition-colors even:bg-slate-50/40 hover:bg-emerald-50/40">
                <td className="border-b border-slate-100 px-4 py-3.5">
                  <IdentityCell
                    id={m.userId}
                    name={m.name || m.email}
                    primary={
                      <Link to={`/users/${m.userId}`} className="hover:text-emerald-700 hover:underline">
                        {m.name || m.email || m.userId}
                      </Link>
                    }
                    secondary={m.name ? m.email ?? undefined : undefined}
                  />
                </td>
                <td className="border-b border-slate-100 px-4 py-3.5 text-slate-700">{humanize(m.role)}</td>
                <td className="border-b border-slate-100 px-4 py-3.5">
                  {m.status === 'active' ? <StatusPill tone="green" label="Active" /> : <StatusPill tone="amber" label={humanize(m.status)} />}
                </td>
                <td className="border-b border-slate-100 px-4 py-3.5 text-slate-600">
                  {m.role === 'owner' && workspaceKind(workspace.type) === 'business'
                    ? 'All (owner)'
                    : '*' in appRoles
                      ? `All apps (${appRoles['*']})`
                      : appCount === 0
                        ? 'None'
                        : `${appCount} app${appCount === 1 ? '' : 's'}`}
                </td>
                <td className="border-b border-slate-100 px-4 py-3.5 text-right">
                  <MemberActions workspace={workspace} member={{ ...m, appRoles }} apps={apps} onChanged={onChanged} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
